import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Edit2, Trash2, Lock, Unlock } from "lucide-react";

const EMPTY = { room_type_id: "", room_number: "", floor: "", notes: "", is_active: true, manual_blocked: false };

export default function RoomUnitsPage() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [types, setTypes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const canManage = user?.role === "super_admin" || user?.role === "admin";

  const load = async () => {
    const [u, t] = await Promise.all([
      api.get(`/admin/room-units${filter !== "all" ? `?room_type_id=${filter}` : ""}`),
      api.get("/room-types?active_only=false"),
    ]);
    setUnits(u.data); setTypes(t.data);
  };
  useEffect(() => { load(); }, [filter]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, room_type_id: types[0]?.id || "" }); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ room_type_id: u.room_type_id, room_number: u.room_number, floor: u.floor || "", notes: u.notes || "", is_active: u.is_active, manual_blocked: u.manual_blocked });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/room-units/${editing.id}`, form);
      else await api.post("/admin/room-units", form);
      toast.success(editing ? "Unit updated" : "Unit created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this unit?")) return;
    try { await api.delete(`/admin/room-units/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (id) => {
    try { await api.post(`/admin/room-units/${id}/toggle-block`); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="room-units-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Room Units</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Every physical room in the hotel (e.g. Room 101), attached to a type.</p>
        </div>
        {canManage && <Button onClick={openNew} data-testid="new-unit-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> New room</Button>}
      </div>

      <div className="flex gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64" data-testid="unit-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Room #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Floor</TableHead>
                <TableHead>Housekeeping</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((u) => (
                <TableRow key={u.id} data-testid={`unit-row-${u.room_number}`}>
                  <TableCell className="font-mono">{u.room_number}</TableCell>
                  <TableCell>{u.room_type_name}</TableCell>
                  <TableCell>{u.floor || "—"}</TableCell>
                  <TableCell>
                    {(() => {
                      const hk = u.housekeeping_status || "ready";
                      const cls = hk === "ready" ? "bg-emerald-100 text-emerald-700" :
                                  hk === "dirty" ? "bg-red-100 text-red-700" :
                                  hk === "cleaning" ? "bg-amber-100 text-amber-700" :
                                  "bg-slate-100 text-slate-700";
                      return <Badge className={`${cls} hover:opacity-90 capitalize`}>{hk}</Badge>;
                    })()}
                  </TableCell>
                  <TableCell>
                    {!u.is_active ? <Badge className="bg-slate-500 text-white">Inactive</Badge> :
                     u.manual_blocked ? <Badge className="bg-red-500 text-white">Blocked</Badge> :
                     <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Available</Badge>}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {canManage && <>
                      <Button size="sm" variant="ghost" onClick={() => toggle(u.id)} data-testid={`toggle-unit-${u.room_number}`}>{u.manual_blocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-unit-${u.room_number}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`delete-unit-${u.room_number}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
              {units.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-[color:var(--muted-text)]">No rooms yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="unit-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit room" : "New room"}</DialogTitle>
            <DialogDescription>Give the room a number and attach it to a type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Room type</Label>
              <Select value={form.room_type_id} onValueChange={(v) => setForm({ ...form, room_type_id: v })}>
                <SelectTrigger data-testid="unit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Room number</Label><Input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} placeholder="e.g. 101" data-testid="unit-number" /></div>
            <div><Label>Floor</Label><Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="e.g. 1" data-testid="unit-floor" /></div>
            <div><Label>Notes (housekeeping)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="unit-notes" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="unit-active" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-unit-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
